import { Component, ElementRef, ViewChild, computed, inject, input, signal } from '@angular/core';
import { formatDistanceToNow } from 'date-fns';
import { de, enUS } from 'date-fns/esm/locale';
import { pt } from 'date-fns/locale';
import { UiService } from '@lp-ui-lib';
import { I18nService } from '@lp-ui-lib';
import { CurrentTelemetry } from '@lp-ui-lib';
import { GLOBAL_CONSTANT } from '@lp-ui-lib';
import { SupportedLanguage } from '@lp-ui-lib';
import { NzIconModule } from 'ng-zorro-antd/icon';

@Component({
  selector: 'app-sensor-value',
  templateUrl: './sensor-value.html',
  styleUrls: ['./sensor-value.scss'],
  imports: [NzIconModule],
})
export class SensorValueComponent {
  public ui = inject(UiService);
  public i18n = inject(I18nService);
  GLOBAL_CONSTANT = GLOBAL_CONSTANT;
  ghost = input<boolean>();
  asHeader = input<boolean>();
  asHealth = input<boolean>();
  lineColor = input<string>();
  telemetry = input<any>();
  graphData = input<any>();
  inactiveOverride = input<object>();
  isSteamTrap = input<boolean>();

  @ViewChild('battery')
  battery: ElementRef<any> | null = null;

  batteryCurrentVal = signal(false);

  isDesktop = computed(() => this.ui.IsDesktop);

  IsBattery = computed(() => {
    const telemetry: CurrentTelemetry | undefined = this.telemetry();
    const titleSignal = telemetry?.title;
    // If title is a signal, call it to get the string value
    const title =
      typeof titleSignal === 'function'
        ? titleSignal
        : (titleSignal ?? this.GLOBAL_CONSTANT.EMPTY_STRING);
    // this.batteryCurrentVal.set(
    //   this.i18n.parse(title) === I18n_keys.BATTERY || this.i18n.parse(title) === I18n_keys.BATTERIE
    // );
    return title.toLocaleLowerCase().includes('battery');
  });

  IsInletOutletTemp = computed(() => {
    const telemetry = this.telemetry();
    const typeId = telemetry?.measurementTypeID;
    // If measurementTypeID is a signal, call it
    const value = typeof typeId === 'function' ? typeId : typeId;
    return (
      value === this.GLOBAL_CONSTANT.MEASUREMENT_ID_INLET_TEMP ||
      value === this.GLOBAL_CONSTANT.MEASUREMENT_ID_OUTLET_TEMP
    );
  });

  InletTemp = computed(() => {
    const telemetrySignal = this.graphData()?.telemetry;
    const data = typeof telemetrySignal === 'function' ? telemetrySignal : (telemetrySignal ?? []);

    return (
      data
        .slice()
        .reverse()
        .find(
          (item: any) => item.measurementTypeID === this.GLOBAL_CONSTANT.MEASUREMENT_ID_INLET_TEMP
        )?.value ?? null
    );
  });

  InletTempString = computed(() => {
    if (!this.graphData() || !this.graphData()?.metadata || this.InletTemp() === null) return null;
    return (Math.round(this.InletTemp() * 100) / 100).toFixed(1) + this.graphData()?.metadata?.unit;
  });

  OutletTemp = computed(() => {
    const telemetrySignal = this.graphData()?.telemetry;
    const data = typeof telemetrySignal === 'function' ? telemetrySignal : (telemetrySignal ?? []);
    return (
      data
        .slice()
        .reverse()
        .find(
          (item: any) => item.measurementTypeID === this.GLOBAL_CONSTANT.MEASUREMENT_ID_OUTLET_TEMP
        )?.value ?? null
    );
  });

  OutletTempString = computed(() => {
    const graphData = this.graphData();
    const metadata = graphData?.metadata;
    const temp = this.OutletTemp();
    return metadata && temp !== null
      ? `${(Math.round(temp * 100) / 100).toFixed(1)}${metadata.unit}`
      : null;
  });

  InletWarning = computed(() => this.telemetry()?.warning ?? false);
  InletCritical = computed(() => this.telemetry()?.critical ?? false);
  OutletWarning = computed(() => this.telemetry()?.warning ?? false);
  OutletCritical = computed(() => this.telemetry()?.critical ?? false);

  get Numblobs(): number {
    if (!this.battery) return 0;
    return +parseInt(
      '' + (this.battery.nativeElement.clientWidth + 8) / (!this.isDesktop() ? 25 : 32),
      10
    );
  }

  get ChargingStatus(): number {
    if (!this.IsBattery()) return 0;
    // 3.3V is 12 (mobile)
    // 1.5 is 4 (mobile)
    //  -> f(x) = x * 40/9 - 8/3
    const x = this.telemetry()['value'].val.min ?? this.telemetry()['value'].val; // val for no minmax values
    const m = 40 / 9;
    const n = -8 / 3;
    let y = x * m + n;
    y = (y / 12) * this.Numblobs; // there can be more or less then 12 blobs, depending on screen size
    return Math.round(y);
  }

  IsGerman = computed(() => this.i18n.languageSignal() === SupportedLanguage.DE);

  Title = computed(() => {
    const telemetry: any = this.telemetry();
    if (this.isWorking() && telemetry?.workingMessage) return telemetry.workingMessage;
    if (this.isInactive() && telemetry?.inactiveMessage) return telemetry.inactiveMessage;
    if (this.isCritical() && telemetry?.criticalMessage) return telemetry.criticalMessage;
    if (this.isWarning() && telemetry?.warningMessage) return telemetry.warningMessage;
    return '';
  });

  isWorking = computed(() => {
    const telemetry: any = this.telemetry();
    return telemetry && !telemetry.warning && !telemetry.critical && !telemetry.inactive;
  });

  isWarning = computed(() => {
    const telemetry = this.telemetry();
    return telemetry?.warning && !telemetry?.critical;
  });

  isInactive = computed(() => this.telemetry()?.inactive ?? false);
  isCritical = computed(() => this.telemetry()?.critical ?? false);

  Range(length: number): number[] {
    if (length < 1) return [];
    return [...Array(length).keys()].map((i) => i + 1);
  }

  get telemetrySafe(): any {
    return this.telemetry() ?? ({} as CurrentTelemetry);
  }

  get valueType(): string {
    const type = this.telemetrySafe?.valueType;
    return typeof type === 'function' ? type : (type ?? '');
  }

  get timestamp(): string {
    const ts = this.telemetrySafe?.Timestamp;
    const date = typeof ts === 'function' ? ts : ts;
    return date instanceof Date ? date.toISOString() : '';
  }

  get valStr(): any {
    return this.telemetrySafe?.value?.valStr;
  }

  validTimeValue(timestamp: string): boolean {
    try {
      this.minutesToNow(timestamp);
      return true;
    } catch {
      return false;
    }
  }

  noData(telemetry: any) {
    if (!telemetry) return true;
    if (telemetry.valueType === 'minmax') {
      return telemetry.value.valStr.min.length + telemetry.value.valStr.max.length === 0;
    }
    return telemetry.value.valStr.length === 0;
  }

  minutesToNow(timestamp: string): string {
    return formatDistanceToNow(new Date(timestamp), {
      locale:
        this.i18n.languageSignal() === SupportedLanguage.DE
          ? de
          : this.i18n.languageSignal() === SupportedLanguage.PT
            ? pt
            : enUS,
    });
  }
}
